import { EmailMessage } from './emailService';
import axios from 'axios';

// Interface para representar um chunk de texto extraído de um documento
export interface TextChunk {
  content: string;
  metadata: {
    source: string;
    page?: number;
    position?: number;
    productId?: string;
    productType?: string;
  };
  embedding?: number[];
  score?: number;
}

// Interface para opções de RAG
export interface RAGOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  personality?: string;
}

// Interface para representar um histórico de e-mails
export interface EmailThread {
  id: string;
  subject: string;
  messages: EmailMessage[];
  lastUpdated: Date;
}

// Interface para resposta gerada por IA
export interface AIEmailResponse {
  subject: string;
  body: string;
  suggestedActions?: string[];
  referencedSources?: TextChunk[];
}

// Função para gerar resposta de e-mail usando IA com RAG
export const generateEmailResponse = async (
  email: EmailMessage, 
  thread: EmailThread | null = null,
  options: RAGOptions = {}
): Promise<AIEmailResponse> => {
  try {
    // Construir o contexto com base no e-mail e na thread (se existir)
    let context = `E-mail recebido de: ${email.from}\nAssunto: ${email.subject}\nConteúdo: ${email.body}\n\n`;
    
    if (thread && thread.messages.length > 0) {
      context += "Histórico de mensagens anteriores:\n";
      // Adicionar até 3 mensagens anteriores para contexto (da mais recente para a mais antiga)
      const previousMessages = thread.messages
        .filter(msg => msg.id !== email.id)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 3);
      
      for (const msg of previousMessages.reverse()) {
        context += `De: ${msg.from}\nPara: ${msg.to}\nData: ${msg.date.toLocaleString()}\nConteúdo: ${msg.body}\n\n`;
      }
    }

    // Consultar a base de conhecimento usando RAG
    const query = `Por favor, gere uma resposta profissional para o seguinte e-mail: "${email.subject}". 
    O conteúdo do e-mail é: "${email.body}".
    A resposta deve ser cordial, profissional e resolver as questões levantadas pelo cliente.`;
    
    // Simulação de busca na base de conhecimento para ambiente de desenvolvimento
    const relevantChunks: TextChunk[] = [];
    
    // Em ambiente de produção, usaríamos o vectorStore real
    if (process.env.NODE_ENV !== 'development') {
      // Aqui implementaríamos a busca real na base de conhecimento
      // const searchResults = await vectorStore.search(query, 5);
      // relevantChunks.push(...searchResults);
    } else {
      // Dados simulados para desenvolvimento
      relevantChunks.push({
        content: "Nossos apartamentos na região central começam a partir de R$ 250.000,00, com financiamento facilitado.",
        metadata: {
          source: "produtos_imoveis",
          position: 1
        }
      });
      
      relevantChunks.push({
        content: "Oferecemos visitas agendadas de segunda a sábado, das 9h às 18h, com acompanhamento de um corretor especializado.",
        metadata: {
          source: "politicas_atendimento",
          position: 2
        }
      });
    }
    
    // Construir o prompt para a API da OpenAI
    let prompt = `Você é um assistente de e-mail para uma imobiliária. 
    Você deve gerar uma resposta profissional e útil para o seguinte e-mail:
    
    ${context}
    
    `;
    
    if (relevantChunks.length > 0) {
      prompt += `Informações relevantes da base de conhecimento:\n\n`;
      relevantChunks.forEach((chunk: TextChunk, index: number) => {
        prompt += `[${index + 1}] ${chunk.content}\n\n`;
      });
    }
    
    prompt += `
    Gere uma resposta profissional para o e-mail acima.
    A resposta deve incluir:
    1. Uma saudação apropriada
    2. Respostas claras às perguntas ou preocupações levantadas
    3. Próximos passos ou ações recomendadas
    4. Uma assinatura profissional
    
    Se houver informações específicas da base de conhecimento que forem relevantes, incorpore-as na resposta.
    `;

    // Configurar opções para a chamada da API
    const defaultOptions: RAGOptions = {
      model: 'gpt-4',
      temperature: 0.7,
      maxTokens: 1000,
      personality: 'profissional e prestativo'
    };
    
    const mergedOptions = { ...defaultOptions, ...options };

    // Em ambiente de desenvolvimento, simular a resposta da API
    if (process.env.NODE_ENV === 'development') {
      // Simular tempo de processamento
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Gerar uma resposta simulada
      const simulatedResponse: AIEmailResponse = {
        subject: email.subject.startsWith('RE:') ? email.subject : `RE: ${email.subject}`,
        body: `Prezado(a) ${extractName(email.from)},

Obrigado por entrar em contato conosco.

${generateSimulatedResponseBody(email.body)}

Estamos à disposição para qualquer esclarecimento adicional.

Atenciosamente,
Equipe de Vendas
Imobiliária XYZ
Tel: (11) 1234-5678`,
        suggestedActions: [
          'Agendar visita ao imóvel',
          'Enviar mais informações sobre financiamento',
          'Adicionar cliente à lista de leads'
        ],
        referencedSources: relevantChunks
      };
      
      return simulatedResponse;
    }

    // Fazer a chamada à API da OpenAI (em produção)
    const response = await axios.post('https://api.openai.com/v1/chat/completions', {
      model: mergedOptions.model,
      messages: [
        { role: 'system', content: `Você é um assistente de e-mail ${mergedOptions.personality}. Sua tarefa é gerar respostas profissionais para e-mails.` },
        { role: 'user', content: prompt }
      ],
      temperature: mergedOptions.temperature,
      max_tokens: mergedOptions.maxTokens
    }, {
      headers: {
        'Authorization': `Bearer ${process.env.OPENAI_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    // Processar a resposta da API
    const content = response.data.choices[0].message.content;
    
    // Extrair assunto e corpo da resposta
    const subject = email.subject.startsWith('RE:') ? email.subject : `RE: ${email.subject}`;
    
    return {
      subject,
      body: content,
      suggestedActions: extractSuggestedActions(content),
      referencedSources: relevantChunks
    };
  } catch (error) {
    console.error('Erro ao gerar resposta de e-mail:', error);
    throw new Error('Não foi possível gerar uma resposta para o e-mail.');
  }
};

// Função para extrair o nome do remetente
const extractName = (email: string): string => {
  // Tentar extrair o nome se estiver no formato "Nome <email@exemplo.com>"
  const nameMatch = email.match(/^([^<]+)</);
  if (nameMatch && nameMatch[1]) {
    return nameMatch[1].trim();
  }
  
  // Caso contrário, usar a parte antes do @ no e-mail
  const emailParts = email.split('@');
  if (emailParts.length > 0) {
    // Capitalizar a primeira letra e substituir pontos/underscores por espaços
    const name = emailParts[0].replace(/[._]/g, ' ');
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  
  return 'Cliente';
};

// Função para gerar corpo de resposta simulada com base no conteúdo do e-mail
const generateSimulatedResponseBody = (emailBody: string): string => {
  if (emailBody.toLowerCase().includes('preço') || emailBody.toLowerCase().includes('valor')) {
    return `Em resposta à sua pergunta sobre valores, temos imóveis disponíveis em diversas faixas de preço. Para podermos fornecer informações mais precisas, gostaríamos de saber qual região é do seu interesse e qual o seu orçamento aproximado.

Temos apartamentos a partir de R$ 250.000,00 e casas a partir de R$ 350.000,00, dependendo da localização e das características do imóvel.`;
  }
  
  if (emailBody.toLowerCase().includes('visita') || emailBody.toLowerCase().includes('conhecer')) {
    return `Ficamos felizes com seu interesse em visitar nossos imóveis. Temos disponibilidade para agendamentos de segunda a sábado, das 9h às 18h.

Podemos agendar uma visita para o imóvel de seu interesse. Por favor, nos informe qual data e horário seriam mais convenientes para você, assim como o endereço do imóvel que deseja visitar.`;
  }
  
  if (emailBody.toLowerCase().includes('financiamento') || emailBody.toLowerCase().includes('banco')) {
    return `Quanto à sua pergunta sobre financiamento, trabalhamos com todos os principais bancos e instituições financeiras do mercado. Oferecemos suporte completo durante todo o processo de financiamento.

Para iniciarmos o processo, precisaríamos de alguns documentos básicos como comprovante de renda, documentos pessoais e comprovante de residência. Nossa equipe pode auxiliá-lo em todas as etapas, desde a simulação até a aprovação final.`;
  }
  
  return `Recebemos sua mensagem e agradecemos pelo contato. Nossa equipe está analisando sua solicitação e retornaremos com todas as informações necessárias o mais breve possível.

Para agilizar o atendimento, gostaríamos de saber se você já tem algum imóvel específico em mente ou se está buscando recomendações baseadas em suas necessidades.`;
};

// Função para extrair ações sugeridas da resposta
const extractSuggestedActions = (content: string): string[] => {
  const suggestedActions = [];
  
  if (content.toLowerCase().includes('visita') || content.toLowerCase().includes('agendar')) {
    suggestedActions.push('Agendar visita ao imóvel');
  }
  
  if (content.toLowerCase().includes('financiamento') || content.toLowerCase().includes('banco')) {
    suggestedActions.push('Enviar informações sobre financiamento');
  }
  
  if (content.toLowerCase().includes('documentos') || content.toLowerCase().includes('documentação')) {
    suggestedActions.push('Solicitar documentação necessária');
  }
  
  if (content.toLowerCase().includes('preço') || content.toLowerCase().includes('valor')) {
    suggestedActions.push('Enviar tabela de preços atualizada');
  }
  
  // Adicionar uma ação padrão se nenhuma for detectada
  if (suggestedActions.length === 0) {
    suggestedActions.push('Adicionar cliente à lista de leads');
  }
  
  return suggestedActions;
};

// Função para processar e-mails para a base de conhecimento
export const processEmailsForKnowledge = async (emails: EmailMessage[]): Promise<void> => {
  try {
    // Converter e-mails em chunks para a base de conhecimento
    const chunks: TextChunk[] = emails.map(email => ({
      content: `E-mail de ${email.from} para ${email.to} em ${email.date.toLocaleString()}. 
      Assunto: ${email.subject}. 
      Conteúdo: ${email.body}`,
      metadata: {
        source: `email_${email.id}`,
        position: 0
      }
    }));
    
    // Simulação de adição à base de conhecimento
    if (process.env.NODE_ENV !== 'development') {
      // Aqui implementaríamos a adição real à base de conhecimento
      // await vectorStore.addChunks(chunks);
    }
    
    console.log(`${emails.length} e-mails processados e adicionados à base de conhecimento.`);
  } catch (error) {
    console.error('Erro ao processar e-mails para a base de conhecimento:', error);
    throw new Error('Não foi possível processar os e-mails para a base de conhecimento.');
  }
};

// Função para classificar e-mails por assunto/intenção
export const classifyEmail = async (email: EmailMessage): Promise<{
  category: string;
  confidence: number;
  suggestedResponse?: string;
}> => {
  try {
    // Em ambiente de desenvolvimento, simular a classificação
    if (process.env.NODE_ENV === 'development') {
      // Simular tempo de processamento
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Categorias possíveis
      const categories = [
        'Solicitação de Informação',
        'Agendamento de Visita',
        'Dúvida sobre Financiamento',
        'Reclamação',
        'Proposta de Compra',
        'Solicitação de Documentação',
        'Outros'
      ];
      
      // Lógica simples de classificação baseada em palavras-chave
      let category = 'Outros';
      let confidence = 0.7;
      
      if (email.subject.toLowerCase().includes('visita') || email.body.toLowerCase().includes('visita') || 
          email.body.toLowerCase().includes('conhecer') || email.body.toLowerCase().includes('agendar')) {
        category = 'Agendamento de Visita';
        confidence = 0.85;
      } else if (email.subject.toLowerCase().includes('financiamento') || email.body.toLowerCase().includes('financiamento') ||
                email.body.toLowerCase().includes('banco') || email.body.toLowerCase().includes('crédito')) {
        category = 'Dúvida sobre Financiamento';
        confidence = 0.9;
      } else if (email.subject.toLowerCase().includes('informação') || email.body.toLowerCase().includes('informação') ||
                email.body.toLowerCase().includes('dúvida') || email.body.toLowerCase().includes('preço')) {
        category = 'Solicitação de Informação';
        confidence = 0.8;
      } else if (email.subject.toLowerCase().includes('reclamação') || email.body.toLowerCase().includes('reclamação') ||
                email.body.toLowerCase().includes('insatisfeito') || email.body.toLowerCase().includes('problema')) {
        category = 'Reclamação';
        confidence = 0.95;
      } else if (email.subject.toLowerCase().includes('proposta') || email.body.toLowerCase().includes('proposta') ||
                email.body.toLowerCase().includes('oferta') || email.body.toLowerCase().includes('comprar')) {
        category = 'Proposta de Compra';
        confidence = 0.85;
      } else if (email.subject.toLowerCase().includes('documento') || email.body.toLowerCase().includes('documento') ||
                email.body.toLowerCase().includes('contrato') || email.body.toLowerCase().includes('papéis')) {
        category = 'Solicitação de Documentação';
        confidence = 0.8;
      }
      
      return {
        category,
        confidence,
        suggestedResponse: category !== 'Outros' ? 
          `Sugestão de resposta para ${category.toLowerCase()}...` : undefined
      };
    }
    
    // Em produção, usar a API da OpenAI para classificação
    const response = await axios.post('https://api.openai.com/v1/chat/completions', {
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: `Você é um assistente especializado em classificar e-mails para uma imobiliária. 
          Classifique o e-mail nas seguintes categorias:
          - Solicitação de Informação
          - Agendamento de Visita
          - Dúvida sobre Financiamento
          - Reclamação
          - Proposta de Compra
          - Solicitação de Documentação
          - Outros
          
          Retorne apenas o nome da categoria e um valor de confiança entre 0 e 1.` 
        },
        { 
          role: 'user', 
          content: `Assunto: ${email.subject}\n\nCorpo do e-mail: ${email.body}` 
        }
      ],
      temperature: 0.3,
      max_tokens: 50
    }, {
      headers: {
        'Authorization': `Bearer ${process.env.OPENAI_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });
    
    const content = response.data.choices[0].message.content;
    const categoryMatch = content.match(/([A-Za-zçãõáéíóúâêîôûàèìòù\s]+):\s*([0-9.]+)/i);
    
    if (categoryMatch) {
      return {
        category: categoryMatch[1].trim(),
        confidence: parseFloat(categoryMatch[2])
      };
    }
    
    return {
      category: 'Outros',
      confidence: 0.5
    };
  } catch (error) {
    console.error('Erro ao classificar e-mail:', error);
    return {
      category: 'Outros',
      confidence: 0.5
    };
  }
};
